import { observable, action/* , computed */ } from 'mobx';
import { pdfViewerService } from './services/index'
import { DATA_TYPE, URL_FOXIT_SIGN } from "Api/constants";
import { baseUrlApis } from 'Api/index';
import * as APP_CONSTANTS from 'Constants/app.constant';
import { MULTI_TABS_TYPES } from 'Components/MultiTabsBar/contant'
import { windowActionFn, blobToString, isValidUrl } from 'Utils/utils';
import { store } from './store.index';

/**
 * @typedef {import('Api/bean/FileData.js').FileData} FileData
 */

class Viewer {
  @observable.ref multiTabsTabPaneActiveKey = null
  @observable.ref multiTabsTabPaneActiveType = MULTI_TABS_TYPES.DOC // doc: 文档, 其他tab
  @observable.ref multiTabsTabPane = []  // {title: String, key: String, content: String | ReactDom}
  @observable isMultiTabsBarInit = false;
  @observable.ref pdfui = null;
  @observable.ref pdfViewer = null;
  @observable viewerStatus = 'prepare'; // prepare done
  @observable.ref resolveFn = null;
  @observable.ref pdfuiTask = {};
  /**@type {FileData} */
  @observable.ref currentFileData = null;
  @observable hasOpenFile = false; //是否打开文件
  @observable.ref editorTabName = '';
  @action initialize(pdfui) {
    return new Promise((resolve, reject) => {
      if (!pdfui) {
        return reject('pdfui is null!!!');
      }
      store.appStore.addAppEventListener(APP_CONSTANTS.SDK_FINISH_LOAD, () => {
        //
        this.viewerStatus = APP_CONSTANTS.SDK_FINISH_LOAD;
      })
      if(this.pdfuiTask.resolve){
        this.pdfuiTask.resolve(pdfui)
      }
      pdfViewerService.initialize(pdfui);
      this.pdfui = pdfui;
      this.pdfui.getPDFViewer().then(viewer => {
        this.pdfViewer = viewer;
        resolve();
      })
    })
  }
  @action initMultiTabsTabPane() {
    this.multiTabsTabPane = [];
    let { fileName, docId } = store.docStore.docInfo;
    let docPanel = {
      title: fileName,
      docId,
      key: docId,
      content: '',
      type: 'doc'
    }
    this.multiTabsTabPane.push(docPanel);
    this.multiTabsTabPaneActiveKey = docPanel.key;
    this.multiTabsTabPaneActiveType = docPanel.type;
    if (!this.isMultiTabsBarInit) {
      this.isMultiTabsBarInit = true;
    }
  }
  @action updateMultiTabsTabPane(paneArr = []) {
    let _multiTabsTabPane = [...this.multiTabsTabPane]
    paneArr.forEach(pane => {
      let index = _multiTabsTabPane.findIndex(_panel => _panel.key === pane.key);
      if (index > -1) {
        _multiTabsTabPane[index] = pane;
      } else {
        _multiTabsTabPane.push(pane);
      }
    })
    this.multiTabsTabPane = _multiTabsTabPane;
  }
  @action resetMultiTabsTabPane() {
    this.multiTabsTabPane = [];
    this.multiTabsTabPaneActiveKey = null;
    this.multiTabsTabPaneActiveType = MULTI_TABS_TYPES.DOC;
  }
  @action updateMultiTabsActiveKey(value, activeType) {
    this.multiTabsTabPaneActiveKey = value;
    this.multiTabsTabPaneActiveType = activeType;
  }
  @action removeMultiTabsTabPane(targetKey) {
    let _multiTabsTabPane = [...this.multiTabsTabPane].filter(_panel => _panel.key !== targetKey);
    this.multiTabsTabPane = _multiTabsTabPane;
  }
  @action setEditorTabName(name){
    this.editorTabName = name;
  }
  _doOpenFile({ file, dataType, fileName }) {
    switch (dataType) {
      case DATA_TYPE.StorageFile:
        return pdfViewerService.openStorageFile(file);
      case DATA_TYPE.File:
        return pdfViewerService.openFile(file, file.name);
      case DATA_TYPE.Url:
        return pdfViewerService.openFileUrl(file, fileName);
      default:
        return Promise.reject(dataType + 'is missing.')
    }
  }
  /**
   *
   *
   * @returns {Promise<any>}
   */
  _getPDFUI(){
    if(!this.pdfuiTask.promise){
      this.pdfuiTask.promise = new Promise((resolve, reject)=>{
        if(this.pdfui){
          resolve(this.pdfui);
        }else{
          this.pdfuiTask.resolve = resolve;
          this.pdfuiTask.reject = reject;
        }
      })  
    }
    return this.pdfuiTask.promise;
  }
  @action doOpenFile(fileData, callback, fileName, isUrl) {
    store.appStore.sendPerformanceInfo({id: 'user-select-file', event:'user-select-file'})
    if (fileData instanceof File) {
      fileData = { file: fileData, dataType: DATA_TYPE.File }
    } else if (fileData instanceof Blob) {
      fileData.name = fileName;
      fileData = { file: fileData, dataType: DATA_TYPE.File }
    } else if (isValidUrl(fileData) || isUrl) {
      fileData = { file: fileData, dataType: DATA_TYPE.Url, fileName }
    }
    if (!this.pdfui) {
      callback && callback('loading');
    }
    return this._getPDFUI().then(()=>{
      return this._doOpenFile(fileData);
    })
  }

  /**
   *
   *
   * @param {Windows.Storage.StorageFile} file
   */
  @action openStorageFile(file) {
    pdfViewerService.openStorageFile(file)
  }

  @action changeFileState(hasOpen) {
    this.hasOpenFile = hasOpen
    // 打开文档后，分享按钮解锁
    if(hasOpen) {
      store.shareStore.setShareDisabled(false)
      window.pdfui.getComponentByName('comment-operate-button-group').then((com) => {
        com && com.enable()
      })
    } else {
      store.shareStore.setShareDisabled(true)
    }
  }
  @action hasDocument(){
    if (!this.pdfViewer || !this.pdfViewer.currentPDFDoc) {
      return false;
    }
    return true;
  }

  @action exportToString({ success, error, process }) {
    return new Promise((resolve, reject) => {
      if (!this.pdfViewer) {
        return reject('no pdf viewer.');
      }
      const pdfDoc = this.pdfViewer.currentPDFDoc;
      const taskName = '';// "convert-pdf-to-accessible-txt";
      const callback = (result) => {
        if (!result) return;
        const { buffer, percent } = result;
        if (percent < 100) {
          // in doing
          process && process(percent);
        } else if (buffer !== void 0) {
          // done
          let blob = new Blob([...buffer], { type: 'text/plain' });
          blobToString(blob).then(({ ret, data }) => {
            if (ret === 0) {
              let szData = data.replace(/\s{1,}/gi, ' ');
              success && success(szData);
              return resolve(szData);
            }
            reject(data);
          })
        }
      };
      //return;
      let pageCount = pdfDoc.info.pageCount;
      let range = '1-5';
      if (pageCount === 1) {
        range = '1';
      } else if (pageCount < 5) {
        range = "1-" + pageCount;
      }
      let pageRange = { filter: "all", string: range };
      pdfDoc.exportToTxt(taskName, pageRange, callback).catch(e => {
        error && error(e)
        reject(e);
      });

    })
  }
  @action doShowAiPanel() {
    store.appStore.emitEvent(APP_CONSTANTS.AI_SHOW_PLUGIN_PANEL)
  }
  @action async doRequestFoxitSign() {
    if (!window.__online__) {
      store.appStore.doNotifyErrors('offline');
      return;
    }
    let foxitsignurl = await baseUrlApis.getBaseUrlBy(URL_FOXIT_SIGN);
    if (foxitsignurl) {
      foxitsignurl += '?utm_source=FPEditor-Cloud';
      windowActionFn(window, 'open', [foxitsignurl])
    }
  }
}

export default Viewer
